// FooterStreaming.js — Pie «streaming» (footer con `modo: 'streaming'`, Learning).
//
// Arriba: logo, descripción y ayuda a la izquierda; a la derecha, columnas que se
// arman solas desde el catálogo de la portada (cursos y categorías) más las
// columnas escritas en el admin. Abajo, la marca en grande recortada contra el
// borde —el elemento memorable— y la línea final con copyright y «Volver arriba».
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));

const Pie = styled.footer`
  --acento: ${p => p.$acento};
  position: relative; overflow: hidden;
  background: ${p => p.$fondo}; color: #d7dbe1;
  font-family: ${FUENTE};
  border-top: 1px solid rgba(255,255,255,.07);
  a, button { color: inherit; font: inherit; }
`;
// Franja de llamada: tarjeta de vidrio con un resplandor del acento.
const Franja = styled.div`
  position: relative; overflow: hidden; isolation: isolate;
  margin: clamp(2.5rem, 5vw, 3.5rem) 4vw 0; padding: clamp(1.5rem, 3vw, 2.2rem) clamp(1.4rem, 3vw, 2.6rem);
  border-radius: 20px; display: flex; align-items: center; justify-content: space-between; gap: 1.5rem 2.5rem; flex-wrap: wrap;
  background: linear-gradient(120deg, rgba(255,255,255,.06), rgba(255,255,255,.02));
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.09);
  &::before {
    content: ''; position: absolute; z-index: -1; width: 520px; height: 520px; right: -140px; top: -300px; border-radius: 50%;
    background: radial-gradient(closest-side, color-mix(in srgb, var(--acento) 55%, transparent), transparent);
    opacity: .55; filter: blur(10px);
  }
  h2 { margin: 0; font-size: clamp(1.4rem, 2.6vw, 2.1rem); font-weight: 800; font-stretch: 80%; line-height: 1.05; color: #fff; letter-spacing: -.01em; }
  p { margin: .45rem 0 0; font-size: .95rem; color: #a7aeb8; max-width: 46ch; }
  .acciones { display: flex; gap: .7rem; flex-wrap: wrap; }
  .acciones a, .acciones button {
    display: inline-flex; align-items: center; gap: .5rem; height: 46px; padding: 0 1.25rem; border-radius: 999px; border: 0;
    font-size: .95rem; font-weight: 700; text-decoration: none; cursor: pointer; white-space: nowrap;
    transition: transform .15s ease, background .2s, box-shadow .2s;
    svg { width: 18px; height: 18px; flex: none; }
  }
  .primario { background: var(--acento); color: #fff !important; box-shadow: 0 10px 28px -12px var(--acento); }
  .primario:hover { transform: translateY(-1px); box-shadow: 0 14px 32px -12px var(--acento); }
  .secundario { background: rgba(255,255,255,.08); color: #fff !important; box-shadow: inset 0 0 0 1px rgba(255,255,255,.18); }
  .secundario:hover { background: rgba(255,255,255,.14); }
  .acciones a:focus-visible, .acciones button:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
  @media (prefers-reduced-motion: reduce) { .acciones a, .acciones button { transition: none; } }
`;

const Arriba = styled.div`
  display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 2fr); gap: 3rem 4rem;
  padding: clamp(3rem, 6vw, 4.5rem) 4vw 2.5rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 2.5rem; }
`;
const Marca = styled.div`
  img { display: block; height: ${p => p.$altoLogo}; width: auto; margin-bottom: 1.2rem; }
  p { margin: 0; max-width: 34ch; font-size: 1rem; line-height: 1.55; color: #9aa1ab; }
  p { max-width: 42ch; font-size: .92rem; line-height: 1.6; }
  .sello {
    display: inline-flex; align-items: center; gap: .6rem; margin-top: 1.4rem; padding: .5rem .9rem .5rem .55rem;
    border-radius: 999px; background: rgba(255,255,255,.05); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
    font-size: .82rem; font-weight: 600; color: #e3e6ea;
  }
  .sello i { display: grid; place-items: center; width: 26px; height: 26px; border-radius: 50%; background: color-mix(in srgb, var(--acento) 20%, transparent); color: var(--acento); }
  .sello svg { width: 15px; height: 15px; }
`;
const Columnas = styled.div`
  display: grid; grid-template-columns: repeat(${p => p.$n}, minmax(0, 1fr)); gap: 2rem;
  @media (max-width: 700px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  h3 { margin: 0 0 1rem; font-size: .95rem; font-weight: 700; color: #fff; }
  ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: .7rem; }
  a, button {
    position: relative; display: inline-block; padding: 0; border: 0; background: none; cursor: pointer; text-align: left;
    font-size: .92rem; color: #9aa1ab; text-decoration: none; line-height: 1.35; transition: color .2s;
  }
  a, button { transition: color .2s, transform .25s cubic-bezier(.2,.8,.2,1); }
  a::before, button::before {
    content: ''; position: absolute; left: -14px; top: .55em; width: 6px; height: 6px; border-radius: 50%;
    background: var(--acento); opacity: 0; transform: scale(.4); transition: opacity .2s, transform .25s;
  }
  a:hover, button:hover, a:focus-visible, button:focus-visible { color: #fff; transform: translateX(10px); }
  a:hover::before, button:hover::before, a:focus-visible::before, button:focus-visible::before { opacity: 1; transform: none; }
  a:focus-visible, button:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; color: #fff; }
  .cat { display: inline-flex; align-items: center; gap: .5rem; }
  .cat i { font-style: normal; font-size: .72rem; color: #6b727d; font-variant-numeric: tabular-nums; }
  @media (prefers-reduced-motion: reduce) { a, button, a::before, button::before { transition: none; } a:hover, button:hover { transform: none; } }
`;
// La marca gigante: ajustada al ancho con SVG (textLength), en contorno fino,
// recortada contra la línea final; al pasar el cursor se llena del acento.
const Gigante = styled.div`
  padding: 0 4vw; margin-top: .5rem; user-select: none; line-height: 0;
  svg { display: block; width: 100%; height: auto; overflow: hidden; }
  text {
    font-family: ${FUENTE}; font-weight: 800; font-stretch: 75%;
    fill: var(--acento); fill-opacity: 0; stroke: rgba(255,255,255,.2); stroke-width: 1.2;
    transition: fill-opacity .8s ease, stroke .6s ease;
  }
  &:hover text { fill-opacity: 1; stroke: transparent; }
  @media (prefers-reduced-motion: reduce) { text { transition: none; } }
`;
const Abajo = styled.div`
  position: relative; display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap;
  padding: 1.1rem 4vw 1.4rem; border-top: 1px solid rgba(255,255,255,.08);
  background: ${p => p.$fondo};
  font-size: .82rem; color: #6b727d;
  button {
    display: inline-flex; align-items: center; gap: .45rem; padding: .45rem .9rem; border: 0; border-radius: 999px; cursor: pointer;
    background: rgba(255,255,255,.06); color: #c9ced6; font-size: .82rem; font-weight: 600; transition: background .2s, color .2s;
    svg { width: 15px; height: 15px; transition: transform .25s ease; }
  }
  button:hover { background: var(--acento); color: #fff; }
  button:hover svg { transform: translateY(-2px); }
  button:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
`;

const FooterStreaming = ({ config = {}, proyecto }) => {
  const c = config;
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const secciones = proyecto?.config_json?.landing_secciones || [];
  const cat = secciones.find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const header = secciones.find(s => s.tipo === 'header')?.config || {};
  const logo = urlImg(c.logo || header.logo);
  const acento = c.color_acento || cat.color_acento || '#E50914';
  const fondo = c.color_fondo || cat.color_fondo || '#141414';
  const docs = proyecto?.documentos || [];

  const cursos = useMemo(() => (cat.items || []).filter(it => it.documento_id).map(it => ({
    key: it.key, titulo: it.titulo_cartelera || it.titulo || docs.find(d => String(d.id) === String(it.documento_id))?.titulo || 'Curso',
  })).slice(0, Number(c.max_cursos) || 6), [cat.items, docs, c.max_cursos]);
  const categorias = useMemo(() => {
    const n = {};
    (cat.items || []).forEach(it => { const g = (it.categoria || '').trim(); if (g) n[g] = (n[g] || 0) + 1; });
    return Object.entries(n);
  }, [cat.items]);

  // Abre la ficha del curso en el catálogo (evento en la portada, ?curso= desde otra página).
  const abrirCurso = (key) => {
    if (pathname === '/') window.dispatchEvent(new CustomEvent('catalogo:abrir', { detail: { key } }));
    else navigate(`/?curso=${encodeURIComponent(key)}`);
  };

  const extra = (c.columnas || []).filter(col => (col.links || []).length);
  const n = (c.mostrar_cursos !== false && cursos.length ? 1 : 0) + (c.mostrar_categorias !== false && categorias.length > 1 ? 1 : 0) + extra.length;
  const marca = c.texto_gigante ?? (c.nombre_marca || proyecto?.nombre || '');

  return (
    <Pie $acento={acento} $fondo={fondo}>
      {c.mostrar_franja !== false && (
        <Franja>
          <div>
            <h2>{c.franja_titulo || 'Aprende a tu ritmo, con certificado'}</h2>
            <p>{c.franja_texto || 'Cursos en video para profesionales de la salud. Empieza hoy y retoma donde quedaste.'}</p>
          </div>
          <div className="acciones">
            <Link className="primario" to={user ? '/contenidos' : '/'} onClick={() => window.scrollTo({ top: 0 })}>
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15a1 1 0 0 0 1.52.85l12-7.5a1 1 0 0 0 0-1.7l-12-7.5A1 1 0 0 0 7 4.5Z" /></svg>
              {user ? (c.franja_boton_sesion || 'Continuar aprendiendo') : (c.franja_boton || 'Ver cursos')}
            </Link>
            {c.correo_ayuda && (
              <a className="secundario" href={`mailto:${c.correo_ayuda}`} title={c.texto_ayuda || '¿Tienes dudas sobre un curso?'}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></svg>
                {c.correo_ayuda}
              </a>
            )}
          </div>
        </Franja>
      )}
      <Arriba>
        <Marca $altoLogo={c.altura_logo || '34px'}>
          {logo && <Link to="/" aria-label="Inicio"><img src={logo} alt={proyecto?.nombre || ''} /></Link>}
          {c.descripcion && <p>{c.descripcion}</p>}
          {c.aval_texto && (
            <span className="sello">
              <i><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg></i>
              {c.aval_texto}
            </span>
          )}
        </Marca>
        <Columnas $n={Math.max(1, Math.min(n, 4))}>
          {c.mostrar_cursos !== false && cursos.length > 0 && (
            <nav aria-label="Cursos">
              <h3>{c.titulo_cursos || 'Cursos'}</h3>
              <ul>{cursos.map(cu => <li key={cu.key}><button type="button" onClick={() => abrirCurso(cu.key)}>{cu.titulo}</button></li>)}</ul>
            </nav>
          )}
          {c.mostrar_categorias !== false && categorias.length > 1 && (
            <nav aria-label="Categorías">
              <h3>{c.titulo_categorias || 'Categorías'}</h3>
              <ul>{categorias.map(([g, k]) => <li key={g}><Link to="/" className="cat">{g}<i>{k}</i></Link></li>)}</ul>
            </nav>
          )}
          {extra.map((col, i) => (
            <nav key={i} aria-label={col.titulo || 'Enlaces'}>
              {col.titulo && <h3>{col.titulo}</h3>}
              <ul>
                {col.links
                  // «Iniciar sesión» sobra con sesión; «Mi perfil», sin ella.
                  .filter(l => !(user && /^\/login/.test(l.url || '')) && !(!user && /^\/(perfil|certificados|contenidos)/.test(l.url || '')))
                  .map((l, j) => (
                    <li key={j}>
                      {/^https?:\/\//.test(l.url || '')
                        ? <a href={l.url} target="_blank" rel="noopener noreferrer">{l.texto}</a>
                        : <Link to={l.url || '/'}>{l.texto}</Link>}
                    </li>
                  ))}
              </ul>
            </nav>
          ))}
        </Columnas>
      </Arriba>
      {marca && (
        <Gigante aria-hidden="true">
          {/* La palabra ocupa 1000 de ancho; el alto recorta la parte baja de las letras. */}
          <svg viewBox="0 0 1000 250" preserveAspectRatio="xMidYMax meet">
            <text x="0" y="292" fontSize="370" textLength="1000" lengthAdjust="spacingAndGlyphs">{marca}</text>
          </svg>
        </Gigante>
      )}
      <Abajo $fondo={fondo}>
        <span>{c.texto_copyright || `© ${new Date().getFullYear()} ${proyecto?.nombre || ''}`}</span>
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })}>
          Volver arriba
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 14 6-6 6 6" /></svg>
        </button>
      </Abajo>
    </Pie>
  );
};

export default FooterStreaming;
